import * as React from "react";
import { Link } from "react-router-dom";
import cn from "classnames";
import { Tooltip, ITooltipProps } from "../Tooltip";
import styles from "./styles.module.scss";

export interface IProps {
    accessibilityAttributes?: {
        role?: string;
        "data-testid"?: string;
        "aria-checked"?: React.AriaAttributes["aria-checked"];
        "aria-invalid"?: React.AriaAttributes["aria-invalid"];
    };
    children: React.ReactNode;
    className?: string;
    color?: "primary" | "secondary" | "light-secondary" | "tertiary";
    disabled?: boolean;
    download?: boolean;
    externalLink?: boolean;
    href?: string;
    name?: string;
    onClick?: () => void;
    shape?: "regular" | "round";
    style?: React.CSSProperties;
    title?: string;
    tooltip?: string;
    tooltipPlacement?: ITooltipProps["placement"];
    type?: "submit" | "button";
    variant: "filled" | "outlined" | "text";
}

export const Button = React.forwardRef(function Button(p: IProps, ref) {
    const className = cn(p.className, styles.button, {
        [styles.colorPrimary]: p.color === "primary",
        [styles.colorSecondary]: p.color === "secondary",
        [styles.colorLightSecondary]: p.color === "light-secondary",
        [styles.colorTertiary]: p.color === "tertiary",
        [styles.variantFilled]: p.variant === "filled",
        [styles.variantOutlined]: p.variant === "outlined",
        [styles.variantText]: p.variant === "text",
        [styles.disabled]: p.disabled,
        [styles.round]: p.shape === "round",
    });

    const externalLink = p.externalLink ?? true;

    let button;

    if (p.href) {
        if (externalLink) {
            button = (
                <a
                    className={className}
                    style={p.style}
                    href={p.href}
                    {...(externalLink
                        ? {
                              rel: "noopener noreferrer",
                              target: "_blank",
                          }
                        : {})}
                    download={p.download}
                    ref={(ref as unknown) as React.RefObject<HTMLAnchorElement>}
                    {...(p.accessibilityAttributes
                        ? p.accessibilityAttributes
                        : {})}
                    title={p.title}
                >
                    {p.children}
                </a>
            );
        } else {
            button = (
                <Link
                    to={p.href}
                    className={className}
                    style={p.style}
                    ref={(ref as unknown) as React.RefObject<HTMLAnchorElement>}
                    {...(p.accessibilityAttributes
                        ? p.accessibilityAttributes
                        : {})}
                    title={p.title}
                >
                    {p.children}
                </Link>
            );
        }
    } else {
        button = (
            <button
                aria-label={p.name}
                className={className}
                style={p.style}
                disabled={p.disabled}
                onClick={p.onClick}
                type={p.type}
                title={p.title}
                ref={(ref as unknown) as React.RefObject<HTMLButtonElement>}
                {...(p.accessibilityAttributes
                    ? p.accessibilityAttributes
                    : {})}
            >
                {p.children}
            </button>
        );
    }

    if (p.tooltip) {
        return (
            <Tooltip content={p.tooltip} placement={p.tooltipPlacement}>
                <span className={styles.buttonWithTooltipWrapper}>
                    {button}
                </span>
            </Tooltip>
        );
    } else {
        return button;
    }
});
